import React, { useEffect, useState } from 'react';
import styles from './addcart.module.scss';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useRouter } from 'next/router';
import { Tooltip } from '@chakra-ui/tooltip';
import { IoIosArrowDown } from 'react-icons/io';
import { Button } from '../Button';
import Typography from '../Typography';
import { Radio } from '../Radio';
import { getProductIdBySlug } from '../../data/products';

interface HostData {
  _id: string;
  name: string;
  displayName: string;
  product: string;
  linux: boolean;
  windows: boolean;
  mac: boolean;
  prebuilt: {
    usd: number;
    eur: number;
  };
  sourcecode: {
    usd: number;
    eur: number;
  };
  subItems?: {
    windows: string[];
    mac: string[];
    linux: string[];
  };
}

const FreeTrial = ({ data, onClose, onSuccess }: any) => {
  const [freeTrialTags, setFreeTrialTags] = useState<{
    [key: string]: boolean;
  }>({});
  const [trial, setTrial] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hosts, setHosts] = useState<HostData[]>([]);
  const [trialselect, setTrialSelect] = useState('');
  const [forceRender, SetforceRender] = useState(false);
  const [trialExhausted, setTrialExhausted] = useState(false);
  const [api, setApi] = useState<any>({});
  const [activeTab, setActiveTab] = useState('windows');
  const router = useRouter();
  const slug = (router.query.url as string) || '';
  const id = slug ? getProductIdBySlug(slug) || null : null;

  const getTabDisplayName = (tab: string) => {
    switch (tab) {
      case 'windows':
        return 'Windows';
      case 'mac':
        return 'Mac';
      case 'linux':
        return 'Linux';
      default:
        return '';
    }
  };

  useEffect(() => {
    setTrialSelect('');
    if (typeof window === 'undefined') return;

    let osType = 'windows';

    if (navigator.appVersion.indexOf('Mac') !== -1) osType = 'mac';
    else if (
      navigator.appVersion.indexOf('X11') !== -1 ||
      navigator.appVersion.indexOf('Linux') !== -1
    )
      osType = 'linux';
    else if (
      navigator.appVersion.indexOf('X11') !== -1 ||
      navigator.appVersion.indexOf('Win') !== -1
    )
      osType = 'windows';

    setTrialSelect(osType);
  }, []);

  const getHosts = async () => {
    try {
      const token = localStorage.getItem('auth-token');
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/host?product=${id}`,
        {
          headers: {
            Authorization: `${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response?.data?.success && response?.data?.data) {
        setHosts(response?.data?.data);
        if (trialExhausted && api.supports) {
          const initialTags = response?.data?.data?.reduce((acc: any, host: HostData) => {
            acc[host.name] = api.supports?.includes(host.name);
            return acc;
          }, {});
          setFreeTrialTags(initialTags);
        } else {
          // Initialize all tags as false for new users
          const initialTags = response?.data?.data.reduce((acc: any, host: HostData) => {
            acc[host.name] = false;
            return acc;
          }, {});
          setFreeTrialTags(initialTags);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load available hosts.');
    }
  };

  useEffect(() => {
    if (id) {
      getHosts();
    }
    // getHosts intentionally excluded: it's recreated every render and should
    // only re-run when `id` changes, not on every trialExhausted/api update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getFreeTrial = async () => {
    try {
      const token = localStorage.getItem('auth-token');
      // Get slug from URL path and find product id
      const currentSlug = (router.query.url as string) || '';
      const productId = currentSlug ? getProductIdBySlug(currentSlug) : null;
      if (token && productId) {
        const { data } = await axios.post(
          `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/trial-access-product`,
          { product: productId },
          {
            headers: {
              Authorization: `${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        return data;
      }
    } catch (error) {}
  };

  useEffect(() => {
    const initializeData = async () => {
      const trialData = await getFreeTrial();

      if (trialData?.data && Object.keys(trialData?.data).length) {
        setTrialExhausted(true);
        const previousOs = trialData?.data?.os || 'linux';
        setTrialSelect(previousOs);
        setActiveTab(previousOs);
        setApi(trialData?.data);
        // Don't set freeTrialTags here, let getHosts handle it
      }

      // await getHosts();
    };

    initializeData();
    // getFreeTrial intentionally excluded: it's recreated every render and should
    // only re-run when forceRender changes, not on every router.query update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRender]);

  useEffect(() => {
    if (hosts.length > 0 && !trialExhausted) {
      const firstCompatibleHost = hosts.find((host) => host.windows);

      if (firstCompatibleHost) {
        setFreeTrialTags((prev) => ({
          ...prev,
          [firstCompatibleHost.name]: true,
        }));
      }
    }
  }, [hosts, trialExhausted]);

  useEffect(() => {
    if (trialExhausted && trialselect) {
      setActiveTab(trialselect);
    }
  }, [trialExhausted, trialselect]);

  useEffect(() => {
    if (!trialExhausted || hosts.length === 0) return;

    const selectedSupports = api.supports || [];
    const updatedTags = hosts.reduce((acc: { [key: string]: boolean }, host) => {
      acc[host.name] = selectedSupports.includes(host.name);
      return acc;
    }, {});

    // Ensure at least one host stays selected if API returned something empty for some reason
    if (!Object.values(updatedTags).some(Boolean) && hosts[0]) {
      updatedTags[hosts[0].name] = true;
    }

    setFreeTrialTags(updatedTags);
  }, [trialExhausted, hosts, api]);

  const handleIconClick = (os: string) => {
    if (trialExhausted) return;
    setTrialSelect(os);

    // Find first compatible host for the selected OS
    const firstCompatibleHost = hosts.find((host) => {
      switch (os) {
        case 'windows':
          return host.windows;
        case 'mac':
          return host.mac;
        case 'linux':
          return host.linux;
        default:
          return false;
      }
    });

    // Create new tags object with all false
    const newTags: { [key: string]: boolean } = {};
    Object.keys(freeTrialTags).forEach((key) => {
      newTags[key] = false;
    });

    // Set the first compatible host to true if found
    if (firstCompatibleHost) {
      newTags[firstCompatibleHost.name] = true;
    }

    setFreeTrialTags(newTags);
  };

  const isHostCompatible = (host: HostData) => {
    if (trialExhausted) {
      return api.supports?.includes(host.name) && api.os === trialselect;
    }

    switch (trialselect) {
      case 'windows':
        return host.windows;
      case 'mac':
        return host.mac;
      case 'linux':
        return host.linux;
      default:
        return false;
    }
  };

  const toggleFreeTrialTag = (host: HostData) => {
    if (!isHostCompatible(host) || trialExhausted) {
      return;
    }
    if (
      freeTrialTags[host.name] &&
      Object.values(freeTrialTags).filter((value) => value).length === 1
    ) {
      // toast.error("One support option must be selected");
      return;
    }

    setFreeTrialTags({
      [host.name]: !freeTrialTags[host.name],
    });
  };

  const handleFreeTrial = async () => {
    const token = localStorage.getItem('auth-token');
    if (!token) {
      // setShowlogin(true);
      localStorage.setItem('redirectTo', router.asPath);
      router.push('/login');
      return;
    }

    setTrial(true);
    setLoading(true);
    try {
      // Get slug from URL path and find product id
      const currentSlug = (router.query.url as string) || '';
      const productId = currentSlug ? getProductIdBySlug(currentSlug) : null;

      if (!productId) {
        toast.error('Product not found.');
        setLoading(false);
        return;
      }

      const selectedFreeTrialSupports = Object.entries(freeTrialTags)
        .filter(([_, isSelected]) => isSelected)
        .map(([tagName]) => tagName);

      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/trial-access-request`,
        {
          product: productId,
          supports: selectedFreeTrialSupports,
          os: trialselect,
        },
        {
          headers: {
            Authorization: `${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const alreadyExists = Boolean(response.data?.alreadyExists);
      SetforceRender((prev) => !prev);
      toast.success(
        alreadyExists
          ? "You've already requested a free trial. We'll email you as soon as your download is ready."
          : "Your free trial request has been submitted. We'll email you as soon as your download is ready."
      );
      onSuccess?.();
      onClose?.();
      // getLink(trialselect);
      // dispatch({ type: REQUEST_MASTER_USER });
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setTrial(false);
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.tabs}>
        {['windows', 'mac', 'linux'].map((tab) => {
          const isActive = activeTab === tab;
          const tabClasses = [
            styles.tabButton,
            isActive ? styles.active : '',
            trialExhausted ? styles.tabDisabled : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <Typography
              key={tab}
              variant="text-sm"
              as="p"
              color="secondary"
              weight="normal"
              className={tabClasses}
              role="button"
              aria-disabled={trialExhausted}
              tabIndex={trialExhausted ? -1 : 0}
              onClick={
                trialExhausted
                  ? undefined
                  : () => {
                      setActiveTab(tab);
                      handleIconClick(tab);
                    }
              }
            >
              {getTabDisplayName(tab)}
            </Typography>
          );
        })}
      </div>

      <div className={styles.freeTrialHostDiv}>
        {hosts
          .filter((host) => isHostCompatible(host))
          .map((host) => (
            <Button
              key={host._id}
              className={`${styles.freeTrialHostButton} ${
                freeTrialTags[host.name] ? styles.selectedTag : ''
              }`}
              onClick={() => {
                toggleFreeTrialTag(host);
              }}
              variant="secondary"
            >
              <div className={styles.hostItemContainer}>
                <div className={styles.hostNameContainer}>
                  <Radio
                    checked={freeTrialTags[host.name]}
                    onChange={(checked) => {
                      if (checked) {
                        setFreeTrialTags({ [host.name]: true });
                      }
                    }}
                    size="sm"
                    color="primary"
                  />
                  <Typography
                    variant="text-sm"
                    as="p"
                    color="grayDark-300"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {host.displayName}
                    {host.subItems && (
                      <Typography variant="text-sm" as="p" color="grayDark-400">
                        including:
                      </Typography>
                    )}
                  </Typography>
                </div>
                {/* SubItems Row */}
                {host.subItems && (
                  <div className={styles.subItemsRow}>
                    {hosts.map((host) =>
                      host.subItems ? (
                        <div key={host._id} className={styles.hostSubItems}>
                          {host.subItems[trialselect as keyof typeof host.subItems]?.map(
                            (subItem: string, index: number) => (
                              <Typography
                                variant="text-xs"
                                color="grayDark-300"
                                key={index}
                                className={styles.subItem}
                              >
                                {subItem}
                              </Typography>
                            )
                          )}
                        </div>
                      ) : (
                        <></>
                      )
                    )}
                  </div>
                )}
              </div>
            </Button>
          ))}
      </div>

      {trialExhausted && (
        <Typography
          as="p"
          variant="text-sm"
          color="grayDark-400"
          style={{ marginTop: '12px', textAlign: 'center' }}
        >
          You have already requested a free trial for {getTabDisplayName(trialselect)}. Request
          button is disabled until your current request is processed.
        </Typography>
      )}

      <div className={styles.freeTrialBtnDiv}>
        <Button
          variant="secondary"
          onClick={() => {
            onClose();
          }}
          fullWidth
          label="Cancel"
          disabled={loading}
        />
        <Button
          variant="primary"
          onClick={handleFreeTrial}
          fullWidth
          label={loading ? 'Requesting Free Trial...' : 'Request Free Trial'}
          disabled={trial}
          loading={loading}
        />
      </div>
    </>
  );
};

export default FreeTrial;
